import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { and, eq, gt, or, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { timeout, task } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { fn, get, hash } from '@ember/helper';
import { macroCondition, isDevelopingApp } from '@embroider/macros';

import Flex from 'core/components/flex';
import { AlignItem, FlexDirection, FlexGap } from 'core/utils/flex';
import Time from 'core/components/time';
import {
  HdsAlert,
  HdsButton,
  HdsFormTextInputField,
  HdsIcon,
  HdsModal,
  HdsPaginationCompact,
  HdsFormRadioField,
  HdsTable,
  HdsTextBody,
  HdsTextDisplay,
  HdsTooltipButton,
} from '@hashicorp/design-system-components/components';
import { ProjectServiceListScopeTypeEnum } from '@clients/cloud-resource-manager';
import Loading from 'core/components/loading';

import { optional } from '@nullvoxpopuli/ember-composable-helpers';

import type AbilitiesService from 'ember-can/services/abilities';
import type ApiService from 'api/services/api';
import type FlashMessagesService from 'core/services/flash-messages';
import type {
  HashicorpCloudResourcemanagerOrganization,
  HashicorpCloudResourcemanagerProject,
  ProjectServiceListRequest,
} from '@clients/cloud-resource-manager';
import type { IntlService } from 'ember-intl';
import type Owner from '@ember/owner';

export interface ManageAccessModalChangeProjectSignature {
  Args: {
    hasAccessToProjectLevel?: (args: {
      project?: HashicorpCloudResourcemanagerProject;
      permissions?: string[];
    }) => boolean;
    onClose?: () => void;
    onSubmit?: (args: {
      selectedProject: HashicorpCloudResourcemanagerProject;
    }) => void;
    organization: HashicorpCloudResourcemanagerOrganization;
    testIamPermissions?: string[];
    subheaderText: string;
  };
  Element: HTMLElement;
}

export const CHANGE_PROJECT_LIST_FIXED_PAGE_SIZE = 10;
export const CHANGE_PROJECT_LIST_DEBOUNCE_MS = 250;

export default class ManageAccessModalChangeProject extends Component<ManageAccessModalChangeProjectSignature> {
  @service declare readonly abilities: AbilitiesService;
  @service declare readonly api: ApiService;
  @service declare readonly flashMessages: FlashMessagesService;
  @service declare readonly intl: IntlService;

  @tracked nextPageToken?: string;
  @tracked previousPageToken?: string;
  @tracked selectedProject?: HashicorpCloudResourcemanagerProject;
  @tracked query?: string;

  constructor(
    owner: Owner,
    args: ManageAccessModalChangeProjectSignature['Args'],
  ) {
    super(owner, args);
    this.fetchProjects.perform();
  }

  fetchProjects = task(
    async (requestParameters?: ProjectServiceListRequest) => {
      const { organization, testIamPermissions = [] } = this.args;
      try {
        const { projects, pagination } =
          await this.api.resourceManager.project.projectServiceList(
            ProjectServiceListScopeTypeEnum.ORGANIZATION,
            organization.id,
            CHANGE_PROJECT_LIST_FIXED_PAGE_SIZE,
            requestParameters?.paginationNextPageToken,
            requestParameters?.paginationPreviousPageToken,
            requestParameters?.sortingOrderBy,
            requestParameters?.query,
          );
        this.previousPageToken = pagination?.previousPageToken;
        this.nextPageToken = pagination?.nextPageToken;

        let resourcePermissions;
        try {
          ({ resourcePermissions } =
            await this.api.resourceManager.authorization.authorizationServiceBatchTestIamPermissions(
              {
                resources: projects?.map((project) => {
                  return {
                    permissions: [
                      'resource-manager.projects.create',
                      'resource-manager.projects.delete',
                      'resource-manager.projects.get',
                      'resource-manager.projects.get-iam-policy',
                      'resource-manager.projects.list',
                      'resource-manager.projects.set-iam-policy',
                      'resource-manager.projects.set-system-managed-iam-policy',
                      'resource-manager.projects.update',
                      ...testIamPermissions,
                    ],
                    resourceName: `project/${project.id}`,
                  };
                }),
              },
            ));
        } catch (e) {
          if (macroCondition(isDevelopingApp())) {
            console.error(e);
          }
        }
        return { projects, resourcePermissions };
      } catch (e) {
        if (macroCondition(isDevelopingApp())) {
          console.error(e);
        }
      }
    },
  );

  onSelectProject = (args: {
    project: HashicorpCloudResourcemanagerProject;
    hasAccess: boolean;
  }) => {
    const { project, hasAccess } = args;
    if (!project || !hasAccess) {
      return;
    }
    this.selectedProject = project;
  };

  handleOnInputQuery = task({ restartable: true }, async (event) => {
    const value = event?.target?.value;
    this.query = value;

    await timeout(CHANGE_PROJECT_LIST_DEBOUNCE_MS);

    await this.fetchProjects.perform({
      query: value,
    });
  });

  handleOnSubmit = task(async (event: Event) => {
    event.preventDefault();
    const { onSubmit } = this.args;

    if (!this.selectedProject) {
      return;
    }

    if (onSubmit && typeof onSubmit == 'function') {
      onSubmit({ selectedProject: this.selectedProject });
    }

    this.flashMessages.highlight(
      this.intl.t(
        'manage-access.components.modal.change-project.flash-message.title',
        {
          projectName: this.selectedProject.name,
        },
      ),
      {
        content: this.intl.t(
          'manage-access.components.modal.change-project.flash-message.description',
        ),
      },
    );
  });

  onNextPage = () => {
    this.fetchProjects.perform({
      paginationNextPageToken: this.nextPageToken,
    });
  };

  onPreviousPage = () => {
    this.fetchProjects.perform({
      paginationPreviousPageToken: this.previousPageToken,
    });
  };

  handlePageChange = (pageChange: 'prev' | 'next') => {
    if (pageChange === 'prev') {
      this.onPreviousPage();
    } else {
      this.onNextPage();
    }
  };

  getProjectPermissions = (projectId: string): string[] | undefined => {
    const resourcePermissions =
      this.fetchProjects.last?.value?.resourcePermissions;
    if (!resourcePermissions || !projectId) {
      return undefined;
    }

    const projectPermissionResponse =
      resourcePermissions[`project/${projectId}`];

    return projectPermissionResponse?.allowedPermissions;
  };

  handleHasAccess = (
    project: HashicorpCloudResourcemanagerProject,
    permissions?: string[],
  ): boolean => {
    const { hasAccessToProjectLevel } = this.args;
    if (
      hasAccessToProjectLevel &&
      typeof hasAccessToProjectLevel === 'function'
    ) {
      return hasAccessToProjectLevel({ project, permissions });
    }

    return true;
  };

  get projectsWithPermissions() {
    const projects = this.fetchProjects.last?.value?.projects;

    if (!projects || !Array.isArray(projects)) {
      return [];
    }

    return projects.map((project) => {
      if (!project?.id) return { ...project, hasAccess: false };

      try {
        const permissions = this.getProjectPermissions(project.id);
        const hasAccess = this.handleHasAccess(project, permissions);

        return {
          ...project,
          hasAccess,
        };
      } catch (error) {
        if (macroCondition(isDevelopingApp())) {
          console.error(
            'Error calculating permissions for project:',
            project.id,
            error,
          );
        }
        return { ...project, hasAccess: false };
      }
    });
  }

  <template>
    <HdsModal
      @size="large"
      @onClose={{optional @onClose}}
      data-test-manage-access-modal-change-project
      ...attributes
      as |M|
    >
      <M.Header>
        <Flex
          @alignItems={{AlignItem.Center}}
          @direction={{FlexDirection.Row}}
          @gap={{FlexGap.Md}}
        >
          <HdsIcon @name="swap-horizontal" @size="24" />
          <Flex @direction={{FlexDirection.Column}} @gap={{FlexGap.Xxs}}>
            <HdsTextBody
              @color="faint"
              @size="100"
              @weight="regular"
              data-test-manage-access-modal-change-project-subheader
            >
              {{@subheaderText}}
            </HdsTextBody>
            <HdsTextDisplay
              @color="strong"
              @size="300"
              @weight="semibold"
              data-test-manage-access-modal-change-project-header
            >
              {{t "manage-access.components.modal.change-project.header"}}
            </HdsTextDisplay>
          </Flex>
        </Flex>
      </M.Header>
      <M.Body data-test-manage-access-modal-change-project-body>
        <Flex @direction={{FlexDirection.Column}} @gap={{FlexGap.Md}}>
          <HdsFormTextInputField
            @value={{this.query}}
            @type="search"
            @isLoading={{this.handleOnInputQuery.isRunning}}
            {{on "input" this.handleOnInputQuery.perform}}
            data-test-manage-access-modal-change-project-query
          />
          {{#if this.fetchProjects.isRunning}}
            <Loading
              data-test-manage-access-modal-change-project-loading
              as |L|
            >
              <L.Message>
                {{t "manage-access.components.modal.change-project.loading"}}
              </L.Message>
            </Loading>
          {{else if this.projectsWithPermissions.length}}
            <HdsTable>
              <:head as |H|>
                <H.Tr>
                  <H.Th @width="6%" />
                  <H.Th>
                    {{t
                      "manage-access.components.modal.change-project.table.name"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "manage-access.components.modal.change-project.table.description"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "manage-access.components.modal.change-project.table.creation-date"
                    }}
                  </H.Th>
                </H.Tr>
              </:head>
              <:body as |B|>
                {{#each this.projectsWithPermissions as |project|}}
                  {{#if project.id}}
                    <B.Tr
                      {{on
                        "click"
                        (fn
                          this.onSelectProject
                          (hash project=project hasAccess=project.hasAccess)
                        )
                      }}
                    >
                      <B.Td>
                        {{#if project.hasAccess}}
                          <HdsFormRadioField
                            @name="selected-project"
                            @value={{get project "id"}}
                            checked={{eq this.selectedProject.id project.id}}
                            data-test-manage-access-modal-change-project-radio-field
                          />
                        {{/if}}
                      </B.Td>
                      <B.Td>
                        <Flex
                          @alignItems={{AlignItem.Center}}
                          @direction={{FlexDirection.Row}}
                          @gap={{FlexGap.Xs}}
                        >
                          <span>{{project.name}}</span>
                          {{#unless project.hasAccess}}
                            <HdsTooltipButton
                              @text={{t
                                "manage-access.components.modal.change-project.table.tooltip"
                              }}
                              data-test-manage-access-modal-change-project-name-tooltip
                            >
                              <HdsIcon
                                @name="alert-triangle"
                                @color="warning"
                              />
                            </HdsTooltipButton>
                          {{/unless}}
                        </Flex>
                      </B.Td>
                      <B.Td>{{project.description}}</B.Td>
                      <B.Td>
                        <Time
                          @date={{project.createdAt}}
                          @display="friendly-only"
                        />
                      </B.Td>
                    </B.Tr>
                  {{/if}}
                {{/each}}
              </:body>
            </HdsTable>
            <HdsPaginationCompact
              @isDisabledNext={{not this.nextPageToken}}
              @isDisabledPrev={{not this.previousPageToken}}
              @onPageChange={{this.handlePageChange}}
            />
          {{else}}
            <div>
              {{#if this.query}}
                <HdsAlert @color="neutral" @icon="info" @type="inline" as |A|>
                  <A.Title>
                    {{t
                      "manage-access.components.modal.change-project.alert.no-results"
                    }}
                  </A.Title>
                  <A.Description>
                    {{t
                      "manage-access.components.modal.change-project.alert.try-adjusting-your-applied-filters"
                    }}
                  </A.Description>
                </HdsAlert>
              {{else}}
                <HdsAlert @color="neutral" @icon="info" @type="inline" as |A|>
                  <A.Title>
                    {{t
                      "manage-access.components.modal.change-project.alert.you-have-no-projects"
                    }}
                  </A.Title>
                  <A.Description>
                    {{t
                      "manage-access.components.modal.change-project.alert.create-a-project-to-start"
                    }}
                  </A.Description>
                </HdsAlert>
              {{/if}}
            </div>
          {{/if}}
        </Flex>
      </M.Body>
      <M.Footer data-test-manage-access-modal-change-project-footer>
        <Flex
          @alignItems={{AlignItem.Center}}
          @direction={{FlexDirection.Row}}
          @gap={{FlexGap.Md}}
        >
          <HdsButton
            @text={{t "manage-access.components.modal.change-project.action"}}
            disabled={{or
              (not this.projectsWithPermissions.length)
              this.fetchProjects.isRunning
              this.handleOnSubmit.isRunning
            }}
            type="button"
            data-test-manage-access-modal-change-project-button-submit
            {{on "click" this.handleOnSubmit.perform}}
          />
          <HdsButton
            @text={{t "manage-access.components.modal.change-project.cancel"}}
            @color="secondary"
            disabled={{or
              this.fetchProjects.isRunning
              this.handleOnSubmit.isRunning
            }}
            type="button"
            data-test-manage-access-modal-change-project-button-cancel
            {{on "click" (optional @onClose)}}
          />
          {{#if
            (and
              (gt this.handleOnSubmit.performCount 0) (not this.selectedProject)
            )
          }}
            <Flex
              @alignItems={{AlignItem.Center}}
              @direction={{FlexDirection.Row}}
              @gap={{FlexGap.Xs}}
            >
              <HdsIcon @color="critical" @name="alert-diamond-fill" />
              <span>
                {{t
                  "manage-access.components.modal.change-project.empty-selection"
                }}
              </span>
            </Flex>
          {{/if}}
        </Flex>
      </M.Footer>
    </HdsModal>
  </template>
}
